import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL, API_ENDPOINTS } from "../config/apiconfig";
import api from "../api/axiosInstance";
import { Building2, Mail, Phone, MapPin, Edit, Save, X, Loader2, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "react-toastify";
import "./Profile.css";
import appPreloader from "../services/appPreloader";

const API_URL = `${BASE_URL}${API_ENDPOINTS.PROFILE.GET}`;
const PROFILE_ID = 1;

const Profile = () => {
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    company: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [originalProfile, setOriginalProfile] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Helper to normalize logo data URL for different image formats
  const normalizeLogo = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('data:')) return logo;
    return `data:image/png;base64,${logo}`;
  };

  const fetchLogo = useCallback(async () => {
    try {
      const response = await api.get(`${BASE_URL}${API_ENDPOINTS.SETTINGS.GET}`);
      if (response.data?.logoBase64) {
        setLogoPreview(normalizeLogo(response.data.logoBase64));
      }
    } catch (error) {
      console.error("Error fetching logo:", error);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchLogo();
  }, [fetchLogo]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API_URL}/getById/${PROFILE_ID}`);
      setProfile(res.data);
      setOriginalProfile(res.data);
      setError("");
    } catch (error) {
      console.error("Error fetching profile:", error);
      setError("Failed to fetch profile data");
      toast.error("Failed to fetch profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select PNG, JPG, JPEG, WEBP or SVG image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo size must be less than 5 MB.');
      return;
    }

    setLogoFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleEditSave = async () => {
    if (isEditing) {
      try {
        const updatedProfile = { ...profile };
        const response = await api.put(`${API_URL}/updateById/${PROFILE_ID}`, updatedProfile);
        const savedProfile = response.data && typeof response.data === "object"
          ? { ...updatedProfile, ...response.data }
          : updatedProfile;
        setProfile(savedProfile);
        setOriginalProfile(savedProfile);
        appPreloader.cache.set("profile", savedProfile);
        appPreloader.saveToLocalStorage();
        window.dispatchEvent(new CustomEvent("businessProfileUpdated", { detail: savedProfile }));

        // Save logo to settings API if uploaded
        if (logoFile) {
          try {
            const logoBase64 = await fileToBase64(logoFile);
            await api.put(`${BASE_URL}${API_ENDPOINTS.SETTINGS.UPDATE}`, { logoBase64 });
            setLogoPreview(logoBase64);
            window.dispatchEvent(new CustomEvent("businessLogoUpdated", { detail: logoBase64 }));
          } catch (logoErr) {
            console.error("Error saving logo to settings:", logoErr);
            toast.error("Profile saved but logo upload failed");
          }
        }

        toast.success("Profile updated successfully!");
      } catch (error) {
        console.error("Update failed:", error);
        toast.error("Failed to update profile");
        return;
      }
    }
    setIsEditing(!isEditing);
    setLogoFile(null);
  };

  const handleCancel = () => {
    setProfile(originalProfile);
    setIsEditing(false);
    setLogoFile(null);
    setLogoPreview(null);
  };

  if (loading) {
    return (
      <div className="profileContainer">
        <div className="profileLoading">
          <Loader2 className="spinner" size={32} />
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profileContainer">
      <div className="profileContent">
        {/* Flow Strip */}
        <div className="flowStrip">
          <div className="flowStripPrimary"></div>
          <div className="flowStripSecondary"></div>
        </div>

        {/* Header */}
        <div className="profileHeader">
          <div className="headerLeft">
            <div className="headerBrand">Business Profile</div>
            <div className="headerTitle">Profile Settings</div>
            <div className="headerSubtitle">Manage your business information and contact details</div>
          </div>
          <div className="headerRight">
            <div className="headerActions">
              {isEditing && (
                <button className="actionButton cancelButton" onClick={handleCancel}>
                  <X size={16} />
                  Cancel
                </button>
              )}
              <button 
                className={`actionButton ${isEditing ? "saveButton" : "editButton"}`} 
                onClick={handleEditSave}
              >
                {isEditing ? <Save size={16} /> : <Edit size={16} />}
                {isEditing ? "Save Changes" : "Edit Profile"}
              </button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alertError">
            <X size={18} />
            {error}
          </div>
        )}

        {/* Profile Card */}
        <div className="profileCard">
          <div className="profileCardHeader">
            <div className="profileAvatar">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="profileLogoImage" />
              ) : (
                <Building2 size={28} />
              )}
            </div>
            <div className="profileInfo">
              <div className="profileName">{profile.name || "Business Name"}</div>
            </div>
          </div>

          {/* Logo Upload Section */}
          <div className="profileField logoUploadField">
            <label className="fieldLabel">
              <ImageIcon size={16} className="fieldIcon" />
              Business Logo
            </label>
            {isEditing ? (
              <div className="logoUploadContainer">
                <input
                  type="file"
                  id="profile-logo-upload"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="profile-logo-upload" className="logoUploadButton">
                  <Upload size={16} />
                  {logoPreview ? "Change Logo" : "Upload Logo"}
                </label>
                {logoPreview && (
                  <button
                    type="button"
                    className="logoRemoveButton"
                    onClick={() => {
                      setLogoFile(null);
                      setLogoPreview(null);
                    }}
                  >
                    <X size={14} />
                    Remove
                  </button>
                )}
              </div>
            ) : (
              <div className="logoDisplayContainer">
                {logoPreview ? (
                  <div className="logoPreviewContainer">
                    <img src={logoPreview} alt="Current Logo" className="logoPreview" />
                  </div>
                ) : (
                  <div className="noLogoText">No logo uploaded</div>
                )}
              </div>
            )}
          </div>

          <div className="profileFields">
            <div className="profileField">
              <label className="fieldLabel">
                <Building2 size={16} className="fieldIcon" />
                Business Name
              </label>
              <input
                type="text"
                name="name"
                value={profile.name || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Enter your business name"
                className="fieldInput"
              />
            </div>

            <div className="profileField">
              <label className="fieldLabel">
                <Mail size={16} className="fieldIcon" />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={profile.email || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="business@example.com"
                className="fieldInput"
              />
            </div>

            <div className="profileField">
              <label className="fieldLabel">
                <Phone size={16} className="fieldIcon" />
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={profile.phone || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="+91 9876543210"
                className="fieldInput"
              />
            </div>

            <div className="profileField">
              <label className="fieldLabel">
                <MapPin size={16} className="fieldIcon" />
                Business Address
              </label>
              <textarea
                name="address"
                value={profile.address || ""}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Enter your business address"
                className="fieldInput fieldTextarea"
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
